import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import {
  validatename,
  validateWebsite,
  validateEmail,
  validatePhone,
  validatelocation,
} from "./validation";
import { getCompanyByUser, updateCompanyByUser } from "../../../api/company";

const inputClass =
  "tw-h-11 tw-w-full tw-rounded-lg tw-border tw-border-white/20 tw-bg-[#190033] tw-px-4 tw-text-sm tw-text-white placeholder:tw-text-white/30 focus:tw-outline-none tw-transition-colors";

export default function Studio({ isActor = false }) {
  const { t } = useTranslation();
  const user = useSelector((state) => state.user);
  const userId = user?.id || "0";

  const [formData, setFormData] = useState({
    name: "", website: "", email: "", phone: "", city: "", province: "", country: "",
  });
  const [errors, setErrors] = useState({});
  const [disabled, setDisabled] = useState(true);
  const [hasAgent, setHasAgent] = useState(true);

  useEffect(() => {
    if (userId === "0") return;
    getCompanyByUser(userId)
      .then((data) => {
        if (data) {
          setFormData(data);
          if (data.hasAgent !== undefined) setHasAgent(data.hasAgent);
        }
      })
      .catch((err) => console.error("Fetch Error:", err));
  }, [userId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let error = "";

    switch (name) {
      case "name":
        error = validatename(value) ? "" : t("Name is required");
        break;
      case "website":
        error = value && !validateWebsite(value) ? t("Invalid URL (include http/https)") : "";
        break;
      case "email":
        error = validateEmail(value) ? "" : t("Invalid Email Address");
        break;
      case "phone":
        error = value && !validatePhone(value) ? t("Invalid Phone (10-15 digits)") : "";
        break;
      case "city":
      case "province":
      case "country":
        error = validatelocation(value) ? "" : t("Required field");
        break;
      default:
        break;
    }

    const newErrors = { ...errors, [name]: error };
    setErrors(newErrors);
    setFormData((prev) => ({ ...prev, [name]: value }));
    setDisabled(Object.values(newErrors).some((e) => e !== ""));
  };

  const saveDetails = () => {
    if (hasAgent || !isActor) {
      if (!formData.name || !formData.email) {
        toast.error(t("Please fill in required fields"));
        return;
      }
    }

    const payload = isActor ? { ...formData, hasAgent } : formData;

    updateCompanyByUser(userId, payload)
      .then(() => {
        toast.success(isActor ? t("Representation updated!") : t("Studio updated!"));
        setDisabled(true);
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || t("Update failed"));
      });
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-6">
      {isActor && (
        <div className="tw-flex tw-items-center tw-gap-3">
          <p className="tw-m-0 tw-text-sm tw-text-white/70">{t("Do you have an Agent?")}</p>
          <div className="tw-flex tw-gap-2">
            <button
              onClick={() => { setHasAgent(true); setDisabled(false); }}
              className={`tw-px-5 tw-py-1.5 tw-rounded-lg tw-text-sm tw-font-medium tw-transition-all tw-border-0 tw-cursor-pointer ${
                hasAgent
                  ? "tw-bg-[#712CB0] tw-text-white"
                  : "tw-bg-white/10 tw-text-white/50 hover:tw-bg-white/20"
              }`}
            >
              {t("Yes")}
            </button>
            <button
              onClick={() => { setHasAgent(false); setDisabled(false); }}
              className={`tw-px-5 tw-py-1.5 tw-rounded-lg tw-text-sm tw-font-medium tw-transition-all tw-border-0 tw-cursor-pointer ${
                !hasAgent
                  ? "tw-bg-[#712CB0] tw-text-white"
                  : "tw-bg-white/10 tw-text-white/50 hover:tw-bg-white/20"
              }`}
            >
              {t("No")}
            </button>
          </div>
        </div>
      )}

      {(hasAgent || !isActor) && (
        <div className="tw-grid tw-grid-cols-1 tw-gap-3">
          {[
            { id: "name", label: isActor ? "Agent Name" : "Studio Name", required: true },
            { id: "website", label: isActor ? "Agent Website" : "Studio Website" },
            { id: "email", label: isActor ? "Agent Email" : "Studio Email", required: true },
            { id: "phone", label: "Phone" },
            { id: "city", label: "City", required: true },
            { id: "province", label: "Province", required: true },
            { id: "country", label: "Country", required: true },
          ].map((f) => (
            <div key={f.id}>
              <input
                name={f.id}
                placeholder={t(f.label) + (f.required ? " *" : "")}
                value={formData[f.id] || ""}
                onChange={handleInputChange}
                className={`${inputClass} ${errors[f.id] ? "tw-border-red-500" : "focus:tw-border-[#712CB0]"}`}
              />
              {errors[f.id] && (
                <p className="tw-mt-1 tw-text-xs tw-text-red-400">{errors[f.id]}</p>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="tw-flex tw-justify-end">
        <button
          disabled={disabled}
          onClick={saveDetails}
          className="tw-rounded-xl tw-bg-[#712CB0] hover:tw-bg-[#8B3FD9] tw-px-8 tw-py-2.5 tw-text-sm tw-font-semibold tw-text-white tw-transition-all active:tw-scale-95 disabled:tw-bg-white/10 disabled:tw-text-white/30 disabled:tw-cursor-not-allowed tw-border-0 tw-cursor-pointer"
        >
          {t("Save Changes")}
        </button>
      </div>
    </div>
  );
}
